export type ChatMentionType = 'agent';

export type ComposerPaletteItemKind = ChatMentionType | 'upload';

export interface ChatMention {
  type: ChatMentionType;
  id: string;
  name: string;
}

export interface SlashCommandItem {
  kind: ComposerPaletteItemKind;
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  group: string;
  mentionName?: string;
}

export const COMPOSER_COMMAND_AGENT_IDS = {
  web: 'aa000000-0000-4000-8000-000000000007',
  image: 'aa000000-0000-4000-8000-000000000010',
} as const;

const COMPOSER_COMMAND_AGENT_ID_SET = new Set<string>(
  Object.values(COMPOSER_COMMAND_AGENT_IDS)
);

export function isComposerCommandAgentId(id: string): boolean {
  return COMPOSER_COMMAND_AGENT_ID_SET.has(id);
}

export function isComposerCommandItem(item: SlashCommandItem): boolean {
  return item.group === 'Comandos' && isComposerCommandAgentId(item.id);
}

export function buildComposerSourceItems(): SlashCommandItem[] {
  return [
    {
      kind: 'upload',
      id: 'upload-files',
      title: 'Adicionar fotos e arquivos',
      subtitle: 'Envie do seu computador',
      icon: 'attach_file',
      group: 'Arquivos',
    },
  ];
}

export function buildComposerCommandItems(
  agents: AssistantAgentCatalogItem[]
): SlashCommandItem[] {
  const byId = new Map(agents.map((agent) => [agent.id, agent]));
  const specs: {
    id: string;
    title: string;
    subtitle: string;
    icon: string;
  }[] = [
    {
      id: COMPOSER_COMMAND_AGENT_IDS.web,
      title: 'Pesquisar na web',
      subtitle: 'Buscar informações públicas na internet',
      icon: 'travel_explore',
    },
    {
      id: COMPOSER_COMMAND_AGENT_IDS.image,
      title: 'Gerar imagem',
      subtitle: 'Criar imagem com IA',
      icon: 'image',
    },
  ];

  return specs.flatMap((spec) => {
    const agent = byId.get(spec.id);
    if (!agent) return [];
    return [
      {
        kind: 'agent' as const,
        id: agent.id,
        title: spec.title,
        mentionName: agent.name,
        subtitle: spec.subtitle,
        icon: spec.icon,
        group: 'Comandos',
      },
    ];
  });
}

export interface SlashQuery {
  start: number;
  query: string;
  trigger: '/' | '@';
}

export interface AssistantAgentCatalogItem {
  id: string;
  name: string;
  purpose: string;
  isSystem?: boolean;
}

export function findSlashQuery(text: string, caret: number): SlashQuery | null {
  const before = text.slice(0, Math.max(0, caret));
  const slashIndex = before.lastIndexOf('/');
  const atIndex = before.lastIndexOf('@');
  const start = Math.max(slashIndex, atIndex);
  if (start < 0) return null;

  const trigger = before[start] as '/' | '@';
  const previous = before[start - 1];

  if (trigger === '/') {
    if (previous === ':' || previous === '/') return null;
  } else if (previous && !/\s/.test(previous)) {
    return null;
  }

  const query = before.slice(start + 1);
  if (query.includes('\n') || /\s/.test(query)) return null;

  return { start, query, trigger };
}

export function buildAgentSlashItems(
  agents: AssistantAgentCatalogItem[]
): SlashCommandItem[] {
  return [...agents]
    .filter((agent) => !isComposerCommandAgentId(agent.id))
    .sort((left, right) => Number(Boolean(right.isSystem)) - Number(Boolean(left.isSystem)))
    .map((agent) => ({
      kind: 'agent' as const,
      id: agent.id,
      title: agent.name,
      subtitle: agent.purpose,
      icon: agent.isSystem ? 'auto_awesome' : 'smart_toy',
      group: agent.isSystem ? 'Agentes Wosi' : 'Meus agentes',
    }));
}

/** Resumo curto do purpose para a paleta — busca continua no texto completo. */
export function compactSlashSubtitle(value: string, maxLength = 64): string {
  let text = value.trim();
  if (!text) return '';

  text = text.replace(/^quando o usu[aá]rio\s+/i, '');
  text = text.replace(/^para\s+/i, '');
  if (text.length <= maxLength) return text;

  const cut = text.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(' ');
  const trimmed = (lastSpace > 28 ? cut.slice(0, lastSpace) : cut).trim();
  return trimmed.endsWith('…') ? trimmed : `${trimmed}…`;
}

export function filterSlashItems(
  items: SlashCommandItem[],
  query: string
): SlashCommandItem[] {
  const normalized = normalize(query);
  if (!normalized) return items;

  return items.filter((item) => {
    const haystack = normalize(
      `${item.title} ${item.subtitle} ${item.mentionName ?? ''}`
    );
    return haystack.includes(normalized);
  });
}

export function excludeMentionedSlashItems(
  items: SlashCommandItem[],
  mentions: ChatMention[]
): SlashCommandItem[] {
  const mentionedIds = new Set(
    mentions
      .filter((mention) => mention.type === 'agent')
      .map((mention) => mention.id)
  );
  if (!mentionedIds.size) return items;

  return items.filter(
    (item) => item.kind !== 'agent' || !mentionedIds.has(item.id)
  );
}

export function insertMentionToken(
  text: string,
  start: number,
  caret: number,
  mention: ChatMention
): { text: string; caret: number } {
  const token = mentionToken(mention);
  const next = `${text.slice(0, start)}${token} ${text.slice(caret)}`;
  return {
    text: next,
    caret: start + token.length + 1,
  };
}

export function mentionToken(mention: ChatMention): string {
  return `@${mention.name}`;
}

export function syncMentions(
  text: string,
  mentions: ChatMention[]
): ChatMention[] {
  return mentions.filter((mention) => text.includes(mentionToken(mention)));
}

export function groupSlashItems(
  items: SlashCommandItem[]
): { group: string; items: SlashCommandItem[] }[] {
  const groups: { group: string; items: SlashCommandItem[] }[] = [];

  for (const item of items) {
    const current = groups[groups.length - 1];
    if (current?.group === item.group) {
      current.items.push(item);
      continue;
    }
    groups.push({ group: item.group, items: [item] });
  }

  return groups;
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function highlightMentions(text: string, mentions: ChatMention[]): string {
  if (!text) return '';

  const unique = [...mentions]
    .sort((a, b) => b.name.length - a.name.length)
    .filter(
      (mention, index, list) =>
        list.findIndex((item) => item.name === mention.name) === index
    );

  let html = escapeHtml(text);
  for (const mention of unique) {
    const token = escapeHtml(mentionToken(mention));
    html = html.split(token).join(
      `<span class="chatbot__mention">${token}</span>`
    );
  }

  return html.replace(/\n/g, '<br>');
}

export function remainingTaskAfterMentions(
  text: string,
  mentions: ChatMention[] = []
): string {
  let next = text ?? '';
  const unique = [...mentions]
    .sort((left, right) => right.name.length - left.name.length)
    .filter(
      (mention, index, list) =>
        list.findIndex((item) => item.name === mention.name) === index
    );

  for (const mention of unique) {
    next = next.split(mentionToken(mention)).join(' ');
  }

  return next.replace(/\s+/g, ' ').trim();
}

const STANDBY_TASK_RE =
  /^(oi|ol[aá]|hey|hello|e a[ií]|opa|fala|bom dia|boa tarde|boa noite|tudo bem(?:\??)|td bem|por favor|pfv|obrigad[oa]|valeu|beleza|ok|certo)[\s!.?]*$/i;

/** Draft/bolha só com `@Agente` (ou cumprimento) — não é pedido de tool. */
export function isMentionOnlyMessage(
  text: string,
  mentions?: ChatMention[],
  hasAttachments = false
): boolean {
  if (hasAttachments) return false;
  if (!mentions?.length) return false;
  const task = remainingTaskAfterMentions(text, mentions);
  return !task || STANDBY_TASK_RE.test(task);
}

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}
